import { useQuery, useQueryClient } from '@tanstack/react-query';
import { V1Service } from '@zelos/codegen/apis';
import type {
  MethodEnum,
  ProgrammingLangauge,
  PublishedRetrieveTryout,
  V1TryoutAvailableCouponAssignment,
  DetailUser,
  V1OrganizationPaymentApplicantRequest,
  OrganizationPaymentMethodEnum,
  LocaleEnum,
} from '@zelos/codegen/apis';
import { classificationMeta } from '@zelos/shared/constants/meta';
import { isAfterNow } from '@zelos/shared/lib/date-util';
import { formatNumber } from '@zelos/shared/lib/number-util';
import { useDetailUser, useIsSignedIn } from '@zelos/shared/lib/sign.service';
import {
  del as delFromIdb,
  get as getFromIdb,
  set as setToIdb,
} from 'idb-keyval';
import produce from 'immer';
import {
  atom,
  useAtom,
  useAtomValue,
  useSetAtom,
} from 'jotai';
import {
  selectAtom,
  RESET,
  useResetAtom,
} from 'jotai/utils';
import { useRouter } from 'next/router';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { v5 as uuidv5 } from 'uuid';
import { useCalcPrice } from '@/lib/calc-price.service';
import { showToast } from '@/lib/toast';

export type ApplyTryoutMode = 'normal' | 'organization' | 'employee';

export interface OrganizationApplicants extends V1OrganizationPaymentApplicantRequest {
  id: string;
  emailValidate: string | true | null;
  languageValidate: string | true | null;
  select: boolean;
  user: number;
}

export interface ApplyTryout extends PublishedRetrieveTryout {
  userId: number | null;
  paymentMethod: MethodEnum | OrganizationPaymentMethodEnum | null;
  regulationsAgreed: boolean;
  marketingAgreed: boolean | null;
  selectedCoupon: V1TryoutAvailableCouponAssignment | null;
  selectedProgrammingLanguage: ProgrammingLangauge | null;
  selectedLocale: LocaleEnum;
  organizationApplicants: OrganizationApplicants[] | null;
  mode: ApplyTryoutMode | null;
  answer: string | null;
  secondAnswer: string | null;
}

type ApplyTryoutAction = {
  validateApplicants: (() => Promise<string | true>) | null,
};

const applyTryoutAtomIdbKey = uuidv5(
  'https://certi.programmers.co.kr/apply-tryout-atom/',
  uuidv5.URL,
);

const applyTryoutAtom = atom<ApplyTryout | null>(null);

const applyTryoutActionAtom = atom<ApplyTryoutAction | null>(null);

applyTryoutAtom.onMount = (setAtom) => {
  getFromIdb<ApplyTryout>(applyTryoutAtomIdbKey).then(
    (applyTryoutAtomIdbValue) => {
      applyTryoutAtomIdbValue && setAtom(applyTryoutAtomIdbValue);
    },
  );
};

export const patchApplyTryoutAtom = atom(
  null,
  (get, set, update: Omit<Partial<ApplyTryout>, 'id'> | typeof RESET) => {
    if (update === RESET) {
      set(applyTryoutAtom, null);
      delFromIdb(applyTryoutAtomIdbKey);
      return;
    }

    const oldApplyTryout = get(applyTryoutAtom);
    if (!oldApplyTryout)
      return;

    const newApplyTryout = produce(
      oldApplyTryout,
      (draft) => Object.assign(draft, update),
    );

    setToIdb(applyTryoutAtomIdbKey, newApplyTryout);
    set(applyTryoutAtom, newApplyTryout);
  },
);

export const patchApplyTryoutActionAtom = atom(
  null,
  (get, set, update: Partial<ApplyTryoutAction> | typeof RESET) => {
    if (update === RESET) {
      set(applyTryoutActionAtom, null);
      return;
    }

    const oldApplyTryoutAction = get(applyTryoutActionAtom) ?? defaultApplyTryoutAction;

    const newApplyTryoutAction = produce(
      oldApplyTryoutAction,
      (draft) => Object.assign(draft, update),
    );

    set(applyTryoutActionAtom, newApplyTryoutAction);
  },
);

const createUseApplyTryoutProperty = <T extends keyof ApplyTryout>(property: T) => (
  () => useAtomValue(
    selectAtom<ApplyTryout | null, NonNullable<ApplyTryout[T]> | null>(
      applyTryoutAtom,
      useCallback(
        (tryout) => tryout?.[property] ?? null,
        [],
      ),
    ),
  )
);

const createUseApplyTryoutActionProperty = <T extends keyof ApplyTryoutAction>(property: T) => (
  () => useAtomValue(
    selectAtom<ApplyTryoutAction | null, NonNullable<ApplyTryoutAction[T]> | null>(
      applyTryoutActionAtom,
      useCallback(
        (action) => action?.[property] ?? null,
        [],
      ),
    ),
  )
);

export const useResetApplyTryout = () => {
  const resetApplyTryout = useResetAtom(patchApplyTryoutAtom);
  const resetApplyTryoutAction = useResetAtom(patchApplyTryoutActionAtom);

  return useCallback(() => {
    resetApplyTryout();
    resetApplyTryoutAction();
  }, [resetApplyTryout, resetApplyTryoutAction]);
};

export const useApplyTryoutId = createUseApplyTryoutProperty('id');
export const useApplyTryoutAvailableCoupons = createUseApplyTryoutProperty('availableCoupons');
export const useApplyTryoutClassification = createUseApplyTryoutProperty('classification');
export const useApplyTryoutFee = createUseApplyTryoutProperty('fee');
export const useApplyTryoutPaymentMethod = createUseApplyTryoutProperty('paymentMethod');
export const useApplyTryoutProgrammingLanguages = createUseApplyTryoutProperty('programmingLanguages');
export const useApplyTryoutRegulationsAgreed = createUseApplyTryoutProperty('regulationsAgreed');
export const useApplyTryoutMarketingAgreed = createUseApplyTryoutProperty('marketingAgreed');
export const useApplyTryoutSelectedCoupon = createUseApplyTryoutProperty('selectedCoupon');
export const useApplyTryoutSelectedProgrammingLanguage = createUseApplyTryoutProperty('selectedProgrammingLanguage');
export const useApplyTryoutSite = createUseApplyTryoutProperty('site');
export const useApplyTryoutPlace = createUseApplyTryoutProperty('place');
export const useApplyTryoutStartAt = createUseApplyTryoutProperty('startAt');
export const useApplyTryoutType = createUseApplyTryoutProperty('type');
export const useApplyTryoutMode = createUseApplyTryoutProperty('mode');
export const useApplyTryoutLocales = createUseApplyTryoutProperty('locales');
export const useApplyTryoutSelectedLocale = createUseApplyTryoutProperty('selectedLocale');
export const useApplyTryoutIsApplied = createUseApplyTryoutProperty('isApplied');
export const useApplyTryoutSelectableTryouts = createUseApplyTryoutProperty('selectableTryouts');
export const useApplyTryoutEmployeeVerification = createUseApplyTryoutProperty('tryoutEmployeeVerification');
export const useApplyTryoutOrganizationApplicants = createUseApplyTryoutProperty('organizationApplicants');
export const useApplyTryoutValidateApplicants = createUseApplyTryoutActionProperty('validateApplicants');
export const useApplyTryoutIsPromotion = createUseApplyTryoutProperty('isPromotion');
export const useApplyTryoutShowOrganizationDiscount = createUseApplyTryoutProperty('showOrganizationDiscount');
export const useApplyTryoutFinalFee = createUseApplyTryoutProperty('finalFee');
export const useEntranceOffsetMinutes = createUseApplyTryoutProperty('entranceOffsetMinutes');
export const useApplyTryoutQuestion = createUseApplyTryoutProperty('question');
export const useApplyTryoutSecondQuestion = createUseApplyTryoutProperty('secondQuestion');
export const useApplyTryoutIsQuestionRequired = createUseApplyTryoutProperty('isQuestionRequired');
export const useApplyTryoutAnswer = (() => {
  const useAnswer = createUseApplyTryoutProperty('answer');
  const useSecondAnswer = createUseApplyTryoutProperty('secondAnswer');

  return (property: 'answer' | 'secondAnswer') => {
    return {
      answer: useAnswer,
      secondAnswer: useSecondAnswer,
    }[property]();
  };
})();
export const useRequiredQuestionFulfilled = () => {
  const isQuestionRequired = useApplyTryoutIsQuestionRequired();
  const question = useApplyTryoutQuestion();
  const answer = useApplyTryoutAnswer('answer');
  const secondQuestion = useApplyTryoutSecondQuestion();
  const secondAnswer = useApplyTryoutAnswer('secondAnswer');

  return [
    [question, answer],
    [secondQuestion, secondAnswer],
  ].reduce((status, item) => {
    const [question, answer] = item;
    if (!isQuestionRequired || !question) return status;

    const isQuestionFulfilled = status.isQuestionFulfilled && Boolean(answer);
    const notAnsweredQuestions = [...status.notAnsweredQuestions];

    if (!answer) {
      notAnsweredQuestions.push(question);
    }

    return { isQuestionFulfilled, notAnsweredQuestions };
  }, { isQuestionFulfilled: true, notAnsweredQuestions: [] as string[] });
};

export const useApplyTryoutBill = () => {
  const classification = useApplyTryoutClassification();
  const fee = Number(useApplyTryoutFee());
  const selectedCoupon = useApplyTryoutSelectedCoupon();
  const mode = useApplyTryoutMode();
  const organizationApplicants = useApplyTryoutOrganizationApplicants();
  const employeeVerification = useApplyTryoutEmployeeVerification();
  const isPromotion = useApplyTryoutIsPromotion();
  const finalFee = useApplyTryoutFinalFee();

  const {
    discountByEmployeeFee,
    discountByCoupon: calcDiscountByCoupon,
    price,
  } = useCalcPrice({
    fee: finalFee,
    coupon: selectedCoupon?.coupon,
    employeeFee: mode === 'employee' && employeeVerification?.employeeFee,
  });

  const discountByEmployeeVerification = useMemo(
    () => mode !== 'employee' ? 0 : discountByEmployeeFee,
    [discountByEmployeeFee, mode],
  );

  const discountByCoupon = useMemo(
    () => mode === 'organization' ? 0 : calcDiscountByCoupon,
    [calcDiscountByCoupon, mode],
  );

  const total = useMemo(() => {
    if (isPromotion) return Number(finalFee);
    if (mode === 'organization') return Number(finalFee) * (organizationApplicants?.length ?? 0);
    if (price <= 0) return 0;
    else if (price < 100) return 100;
    return price;
  }, [
    isPromotion,
    finalFee,
    mode,
    organizationApplicants?.length,
    price,
  ]);

  const baseFee = (classification && classificationMeta[classification.code].baseFee) ?? 0;
  return Object.freeze({
    baseFee, // 시험 유형별 기본 가격
    originalFee: fee, // 시험 생성시 입력한 가격
    fee: finalFee, // 단체의 promotion 이나 시험 단가가 적용된 가격
    total, // 실제 결제 금액
    discount: {
      coupon: discountByCoupon,
      employeeVerification: discountByEmployeeVerification,
    },
    pretty: {
      baseFee: formatNumber(baseFee),
      originalFee: formatNumber(fee),
      discount: {
        coupon: formatNumber(discountByCoupon),
        employeeVerification: formatNumber(discountByEmployeeVerification),
      },
      fee: formatNumber(finalFee),
      total: formatNumber(total),
    },
  });
};

export const useCheckVaildApplyTryout = () => {
  const [applyTryout, putApplyTryout] = useAtom(applyTryoutAtom);
  const detailUser = useDetailUser();
  const resetApplyTryout = useResetApplyTryout();
  const patchApplyTryout = useSetAtom(patchApplyTryoutAtom);
  const setApplyTryoutAction = useSetAtom(applyTryoutActionAtom);
  const queryClient = useQueryClient();
  const [tryoutDataReady, setTryoutDataReady] = useState(false);

  const router = useRouter();

  const tryoutId = useMemo(
    () => Number(router.query.tryout_id) || null,
    [router.query.tryout_id],
  );

  const queryEnabled = useMemo(
    () => Number(tryoutId) > 0,
    [tryoutId],
  );

  const {
    data: tryout,
    isFetched,
    refetch,
  } = useQuery(
    ['v1TryoutsPublishedRetrieve', tryoutId],
    () => V1Service.v1TryoutsPublishedRetrieve({ id: tryoutId! }),
    {
      enabled: queryEnabled,
      refetchOnWindowFocus: false,
      staleTime: 30000,
      retry: 1,
    },
  );

  useEffect(() => {
    applyTryout
      && detailUser
      && detailUser.id !== applyTryout.userId
      && (
        resetApplyTryout(),
        refetch()
      );
  }, [
    applyTryout,
    detailUser,
    queryClient,
    resetApplyTryout,
    refetch,
  ]);

  useEffect(() => {
    setTryoutDataReady(false);
    if (!router.isReady) return;
    if (!detailUser) return;

    const kick = (text: string) => {
      showToast({ text, type: 'error' });
      router.replace('/tryouts');
    };

    if (!queryEnabled) return kick('잘못된 접근입니다.');
    if (!isFetched) return;
    if (!tryout) return kick('시험을 찾을 수 없습니다.');
    if (isAfterNow(tryout.registrationStartDatetime)) return kick('시험 접수가 시작되지 않았습니다.');

    applyTryout?.id !== tryout.id
      && setApplyTryoutAction(defaultApplyTryoutAction);

    (
      applyTryout?.classification.code !== tryout.classification.code
      || (applyTryout?.type !== 'SP') !== (tryout.type !== 'SP')
    ) ? putApplyTryout(createDefaultApplyTryout(tryout, detailUser))
      : patchApplyTryout(tryout);

    setTryoutDataReady(true);
  }, [
    applyTryout,
    detailUser,
    isFetched,
    putApplyTryout,
    patchApplyTryout,
    setApplyTryoutAction,
    queryEnabled,
    router,
    tryout,
  ]);

  return {
    refetchApplyTryout: refetch,
    tryoutDataReady,
  };
};

export const useClearApplyTryoutAtomWhenSignedOut = () => {
  const isSignedIn = useIsSignedIn();
  const resetApplyTryout = useResetApplyTryout();

  useEffect(() => {
    isSignedIn === false && resetApplyTryout();
  }, [isSignedIn, resetApplyTryout]);
};

const defaultApplyTryoutAction: ApplyTryoutAction = {
  validateApplicants: null,
};

export const createDefaultApplyTryout = (
  publishedRetrieveTryout: PublishedRetrieveTryout,
  user?: DetailUser,
): ApplyTryout => Object.assign({
  userId: user?.id || null,
  mode: user?.organization
    ? 'organization'
    : publishedRetrieveTryout.tryoutEmployeeVerification
      ? 'employee'
      : 'normal' as ApplyTryoutMode,
  paymentMethod: null,
  regulationsAgreed: false,
  marketingAgreed: null,
  selectedCoupon: null,
  selectedProgrammingLanguage: null,
  selectedLocale: 'ko' as LocaleEnum,
  organizationApplicants: null,
  answer: null,
  secondAnswer: null,
}, publishedRetrieveTryout);

export const applyTryoutAtomRetrieveForTest = () => {
  if (process.env.NODE_ENV !== 'test')
    throw new Error('unreachable atom "applyTryoutAtom"');

  return applyTryoutAtom;
};
